import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney, parseDecimal } from "@/lib/formatters"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures } from "@/features/staff/reports/report-block"
import { bucketLabel, seriesExtent, tStats } from "@/features/staff/stats/format"
import type { Granularity, StatsBucket } from "@/features/staff/stats/types"

/**
 * The trend, and the two decisions in it that matter.
 *
 * **Columns for days, a line for weeks and months.** A day is a discrete event,
 * not a continuous quantity: a line drawn between Sunday and Monday describes a
 * collapse nobody lived through, because a restaurant's week is not noise around
 * a trend — it IS the signal. At week and month scale the buckets are
 * comparable and a line is the honest shape.
 *
 * **The open bucket is drawn and never counted.** It is dotted, it is labelled,
 * and it is excluded from the best/worst/average figures below and from every
 * comparison above. A partial week plotted like a whole one is the lie this
 * screen exists to refuse.
 *
 * Neutral ink throughout — see `Sparkline` for why a sales series carries no
 * hue. The only colour on this card is the status dot the KPI band owns.
 */

const PLOT = { left: 56, right: 1140, top: 20, bottom: 220 }

function scaleY(value: number, max: number) {
  if (max <= 0) return PLOT.bottom
  const y = PLOT.bottom - (value / max) * (PLOT.bottom - PLOT.top)
  return Math.round(y * 10) / 10
}

export function StatsSeriesChart({
  series,
  granularity,
  averagePerBucket,
}: {
  series: StatsBucket[]
  granularity: Granularity
  /** Server-computed, in cents. This screen does not average anything itself. */
  averagePerBucket: string | null
}) {
  if (series.length === 0) return null

  const closed = series.filter((bucket) => !bucket.open)
  // Scaled against every drawn point, the open one included: a partial bucket
  // that overflowed the axis would be worse than one that is visibly short.
  const max = seriesExtent(series)
  const step = (PLOT.right - PLOT.left) / Math.max(series.length - 1, 1)
  const x = (index: number) => Math.round((PLOT.left + index * step) * 10) / 10

  const points = series.map((bucket, index) => ({
    bucket,
    x: x(index),
    y: scaleY(parseDecimal(bucket.subtotal), max),
  }))
  const closedPoints = points.filter((point) => !point.bucket.open)
  const openPoint = points.find((point) => point.bucket.open)
  const last = closedPoints[closedPoints.length - 1]

  const columns = granularity === "day"
  const width = Math.max(6, (PLOT.right - PLOT.left) / series.length - 4)

  // Every other label on a dense axis, always including the first.
  const labelEvery = Math.max(1, Math.ceil(series.length / 7))

  const best = closed.reduce<StatsBucket | null>(
    (top, bucket) => (top === null || parseDecimal(bucket.subtotal) > parseDecimal(top.subtotal) ? bucket : top),
    null
  )
  const worst = closed.reduce<StatsBucket | null>(
    (low, bucket) => (low === null || parseDecimal(bucket.subtotal) < parseDecimal(low.subtotal) ? bucket : low),
    null
  )
  return (
    <Card density="compact" data-testid="stats-series">
      <CardHeader className="border-b">
        <CardTitle>{tStats("series.title")}</CardTitle>
        <CardDescription>{tStats("notes.open_bucket")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <svg
          viewBox="0 0 1152 262"
          className="block w-full overflow-visible"
          role="img"
          aria-label={tStats("series.title")}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const y = PLOT.bottom - fraction * (PLOT.bottom - PLOT.top)
            return (
              <line
                key={fraction}
                x1={PLOT.left}
                y1={y}
                x2={PLOT.right}
                y2={y}
                className="stroke-border"
                strokeWidth="1"
              />
            )
          })}

          {columns ? (
            points.map((point) => (
              <rect
                key={point.bucket.date}
                x={point.x - width / 2}
                y={point.y}
                width={width}
                height={Math.max(0, PLOT.bottom - point.y)}
                rx="4"
                className={point.bucket.open ? "fill-none stroke-muted-foreground" : "fill-foreground"}
                strokeWidth={point.bucket.open ? 1.5 : undefined}
                strokeDasharray={point.bucket.open ? "3 3" : undefined}
              />
            ))
          ) : (
            <>
              <polyline
                points={closedPoints.map((point) => `${point.x},${point.y}`).join(" ")}
                fill="none"
                className="stroke-foreground"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {last && openPoint ? (
                <polyline
                  points={`${last.x},${last.y} ${openPoint.x},${openPoint.y}`}
                  fill="none"
                  className="stroke-foreground opacity-55"
                  strokeWidth="2"
                  strokeDasharray="2 5"
                  strokeLinecap="round"
                />
              ) : null}
              {last ? (
                <circle cx={last.x} cy={last.y} r="4" className="fill-card stroke-foreground" strokeWidth="2" />
              ) : null}
              {openPoint ? (
                <circle
                  cx={openPoint.x}
                  cy={openPoint.y}
                  r="3.5"
                  className="fill-card stroke-muted-foreground"
                  strokeWidth="1.5"
                />
              ) : null}
            </>
          )}

          {openPoint ? (
            <text
              x={openPoint.x}
              y={PLOT.bottom + 20}
              textAnchor="end"
              className="fill-muted-foreground text-[11px]"
            >
              {tStats("series.open")}
            </text>
          ) : null}

          {points.map((point, index) =>
            index % labelEvery === 0 && !point.bucket.open ? (
              <text
                key={`label-${point.bucket.date}`}
                x={point.x}
                y={PLOT.bottom + 20}
                textAnchor="middle"
                className="fill-muted-foreground text-[11px]"
              >
                {bucketLabel(point.bucket.date, granularity)}
              </text>
            ) : null
          )}
        </svg>

        <ReportFigures>
          <TillFigure
            label={tStats("series.best")}
            value={best ? formatMoney(best.subtotal) : "—"}
            hint={best ? bucketLabel(best.date, granularity) : undefined}
            testId="stats-series-best"
          />
          <TillFigure
            label={tStats("series.worst")}
            value={worst ? formatMoney(worst.subtotal) : "—"}
            hint={worst ? bucketLabel(worst.date, granularity) : undefined}
            testId="stats-series-worst"
          />
          <TillFigure
            label={tStats("series.average")}
            value={averagePerBucket === null ? "—" : formatMoney(averagePerBucket)}
            testId="stats-series-average"
          />
        </ReportFigures>
      </CardContent>
    </Card>
  )
}
